import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Label, Row, Sidebar } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { NAVBAR_HEIGHT } from '~/constants/layout';
import { ConfirmPopup } from '~/commonComponents';
import '~/commonComponents/SelectProfilePopup/OfficeSwitcher.scss';
import { getOfficeSwitcherHeaderProps } from '~/commonComponents/SelectProfilePopup/officeSwitcherHeaderProps';
import { OfficeSwitcherPanel } from '~/commonComponents/SelectProfilePopup/OfficeSwitcherPanel';
import { SelectProfessionTypePopup } from '~/components/ShiftRequestRedesign/SelectProfessionType/SelectProfessionTypePopup';
import { useSelectProfessionType } from '~/components/ShiftRequestRedesign/SelectProfessionType/useSelectProfessionType';
import { useUserSelector } from '~/hooks/useUserSelector';
import ArrowIcon from '~/images/arrow-switch-horizontal.png';
import WarningIcon from '~/images/icons/Warning';
import { routes } from '~/routes';
import { useIsReferralProgramVisible } from '~/routes/hooks';
import { useIsWorkerClassificationEnabled, useRequestControls } from '~/selectors';
import {
  fireMenuBarDashboardClicked,
  fireMenuBarProfessionalHubClicked,
  fireMenuBarReferralClicked,
  fireMenuBarScheduleClicked,
} from '~/posthogs/events';
import { POSTHOG_EVENTS, trackPosthogEvent } from '~/services/posthog';
import { searchChildOffice, selectOffice } from '../actions';
import browserHistory from '../browserHistory';
import { ShiftRequestRedesign } from '../constants/ff.posthog';
import { getShouldUseSidebarRequestProfessionalMenu } from '../growthbook';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import { useFeatureFlagVariant } from '../hooks/useFeatureFlagVariant';
import { BriefcaseIcon } from '../images/icons/BriefcaseIcon';
import { CalendarIcon } from '../images/icons/CalendarIcon';
import { DashboardIcon } from '../images/icons/DashboardIcon';
import { GiftIcon } from '../images/icons/GiftIcon';
import { ProfessionalRequestIcon } from '../images/icons/ProfessionalRequestIcon';
import { UserGroup } from '../images/icons/UserGroup';

const navIconColor = (isActive: boolean) => (isActive ? colors.shades[0] : colors.neutral[600]);

/**
 * Keeps the entire sidebar pinned below the navbar as the page scrolls.
 * alignSelf:flex-start prevents flex-stretch from making the sidebar the full
 * Row height (which would defeat sticky — an element already filling its
 * container has nowhere to stick). With flex-start, the sidebar is only as
 * tall as its content (+ minHeight) and sticks at top:NAVBAR_HEIGHT.
 */
const sidebarStickyStyle = {
  position: 'sticky' as const,
  top: NAVBAR_HEIGHT,
  alignSelf: 'flex-start' as const,
};

export default function SideBar() {
  const dispatch = useDispatch();
  const { location } = browserHistory;
  const childOffices = useSelector((state: any) => state.user.childOffices);
  const selectedChildOffice = useSelector((state: any) => state.user.selectedChildOffice);
  const masterOfficeId = useSelector((state: any) => state.user?.user?.id);
  const allPPJobs = useSelector((state: any) => state.pp.allPPJobs);
  const user = useUserSelector();
  const shouldUseShiftRequestRedesign = useFeatureFlagVariant(ShiftRequestRedesign, 'test');

  const allowedRequest = user?.allowedRequest || {};
  const [shiftRequestsUnavailableModalOpen, setShiftRequestsUnavailableModalOpen] = useState(false);
  const [showOfficeSwitcher, setShowOfficeSwitcher] = useState(false);
  const isWorkerClassificationEnabled = useIsWorkerClassificationEnabled();
  const showWorkerClassificationWarning =
    isWorkerClassificationEnabled && !user?.workerClassification?.classificationType;

  const [showProfessionPopup, setShowProfessionPopup] = useState(false);
  const officeState = user?.address?.[0]?.state ?? '';
  const { availableProfessions } = useSelectProfessionType({ officeState });

  const allowedRequestProfessionalMenuStates = getShouldUseSidebarRequestProfessionalMenu();
  const isAllowedRequestProfessionalMenuEnabled =
    allowedRequestProfessionalMenuStates.length > 0 &&
    allowedRequestProfessionalMenuStates.includes(user.address?.[0]?.state);

  const { isPermProductAllowed } = useRequestControls();
  const hasPostings = useSelector((state: any) => state.permanentJobs.hasPostings);
  const hasPublishedPostings = useSelector(
    (state: any) => state.permanentJobs.hasPublishedPostings,
  );
  const shouldShowReferralProgram = useIsReferralProgramVisible();

  const isPostShiftsActive =
    location.pathname.startsWith('/dashboard/shift-request/') ||
    location.pathname.startsWith('/dashboard/multi-day/');

  const handlePostShiftsClick = () => {
    if (showWorkerClassificationWarning) {
      setShiftRequestsUnavailableModalOpen(true);
      return;
    }
    if (isPostShiftsActive) return;
    trackPosthogEvent(POSTHOG_EVENTS.OFFICE_MENU_BAR_SHIFT_REQUEST_CLICKED);
    if (shouldUseShiftRequestRedesign) {
      setShowProfessionPopup(true);
    } else {
      browserHistory.push(routes.multiDayShiftRequest);
    }
  };

  const renderPPLink = () => {
    if (!allPPJobs || allPPJobs.length === 0) return null;

    const isPPActive = location.pathname.includes('/dashboard/permanentJobs');
    const ppRoute =
      allPPJobs.length === 1
        ? `/dashboard/permanentJobs/${allPPJobs[0].id}`
        : '/dashboard/permanentJobs';
    const ppTitle = allPPJobs.length === 1 ? 'Permanent Job' : 'Permanent Jobs';

    return (
      <Sidebar.Item
        icon={<BriefcaseIcon color={navIconColor(isPPActive)} />}
        active={isPPActive}
        onClick={() => browserHistory.push(ppRoute)}
      >
        {ppTitle}
      </Sidebar.Item>
    );
  };

  return (
    <>
      <Sidebar
        data-cy="sidebar"
        style={{
          width: 350,
          // Fill the viewport below the navbar so the sidebar (and its shadow)
          // reach the footer on short pages, while staying sticky on tall ones.
          minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
          ...sidebarStickyStyle,
          // Sit above page content with a right-edge shadow so full-bleed page
          // headers (e.g. the Shift Summary top-card banner) appear to tuck
          // under the sidebar instead of butting flush against it.
          zIndex: 20,
          boxShadow: '6px 0 12px -4px rgba(0, 0, 0, 0.12)',
          // position:sticky creates its own stacking context, so the dropdown's
          // z-index only competes inside the sidebar. While the office switcher
          // is open, lift the whole sidebar above the page content so nothing
          // (e.g. the "< Back" button) paints over the dropdown (GOTU-3780).
          ...(showOfficeSwitcher ? { zIndex: 1000 } : {}),
        }}
      >
        {childOffices && childOffices.length > 0 && (
          <>
            <Sidebar.Header
              data-cy="office-switcher-trigger"
              className="office-switcher-header"
              onClick={() => setShowOfficeSwitcher(true)}
              style={{ cursor: 'pointer' }}
              {...getOfficeSwitcherHeaderProps(selectedChildOffice, masterOfficeId)}
              action={
                <img src={ArrowIcon} height={24} width={24} alt="switch office" />
              }
            />
            {showOfficeSwitcher && (
              <OfficeSwitcherPanel
                onClose={() => setShowOfficeSwitcher(false)}
                onOfficeClicked={(officeId) => dispatch(selectOffice({ officeId }))}
                onSearch={(searchText) => dispatch(searchChildOffice({ searchText }))}
              />
            )}
          </>
        )}

        {(!childOffices || childOffices.length === 0) && <div style={{ height: 40 }} />}

        <Sidebar.Content>
          <Sidebar.Item
            data-cy="sidebar-dashboard"
            icon={<DashboardIcon color={navIconColor(location.pathname === '/')} />}
            active={location.pathname === '/'}
            onClick={() => {
              browserHistory.push('/');
              fireMenuBarDashboardClicked();
            }}
          >
            Dashboard
          </Sidebar.Item>

          {isAllowedRequestProfessionalMenuEnabled && allowedRequest?.tempOrMdbShifts && (
            <Sidebar.Item
              icon={<ProfessionalRequestIcon color={navIconColor(isPostShiftsActive)} />}
              active={isPostShiftsActive}
              onClick={handlePostShiftsClick}
            >
              Post Shifts
            </Sidebar.Item>
          )}

          {renderPPLink()}

          <Sidebar.Item
            icon={<CalendarIcon color={navIconColor(location.pathname === '/dashboard/calendar')} />}
            active={location.pathname === '/dashboard/calendar'}
            onClick={() => {
              browserHistory.push('/dashboard/calendar');
              fireMenuBarScheduleClicked();
            }}
          >
            Schedule
          </Sidebar.Item>

          <Sidebar.Item
            data-cy="sidebar-professionals-hub"
            icon={
              <UserGroup color={navIconColor(location.pathname.includes('/professionals-hub'))} />
            }
            active={location.pathname.includes('/professionals-hub')}
            onClick={() => {
              browserHistory.push('/professionals-hub/favorites-list');
              fireMenuBarProfessionalHubClicked();
            }}
          >
            Professionals Hub
          </Sidebar.Item>

          {getShouldEnforcePermanentJobV2() && isPermProductAllowed && (
            <Sidebar.Item
              icon={
                <BriefcaseIcon
                  color={navIconColor(location.pathname.startsWith('/permanent-jobs'))}
                />
              }
              active={location.pathname.startsWith('/permanent-jobs')}
              onClick={() =>
                browserHistory.push(
                  hasPostings === false
                    ? '/permanent-jobs/value-proposition'
                    : '/permanent-jobs/postings',
                )
              }
            >
              {hasPublishedPostings === false ? (
                <Row align="center" style={{ gap: 4 }}>
                  Permanent Jobs <Label variant="primary" appearance="filled" size="sm">NEW</Label>
                </Row>
              ) : (
                'Permanent Jobs'
              )}
            </Sidebar.Item>
          )}

          {shouldShowReferralProgram && (
            <Sidebar.Item
              icon={<GiftIcon color={navIconColor(location.pathname === '/dashboard/referral')} />}
              active={location.pathname === '/dashboard/referral'}
              onClick={() => {
                browserHistory.push('/dashboard/referral');
                fireMenuBarReferralClicked();
              }}
            >
              Referral Program
            </Sidebar.Item>
          )}
        </Sidebar.Content>
      </Sidebar>

      {shiftRequestsUnavailableModalOpen && showWorkerClassificationWarning && (
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon size={80} width={80} height={80} color={colors.warning[500]} />
            </div>
          )}
          title="Shift Requests Unavailable"
          description="Shift Requests are unavailable until a worker type has been chosen for this office. Please visit the Worker Type Settings page to select a preference."
          descriptionStyle={{ marginBottom: 40 }}
          leftButtonAction={() => setShiftRequestsUnavailableModalOpen(false)}
          leftButtonText="Close"
          leftButtonStyle={{ width: 170 }}
          rightButtonAction={() => {
            setShiftRequestsUnavailableModalOpen(false);
            browserHistory.push(routes.workerClassificationType);
          }}
          rightButtonText="Set Preference"
          rightButtonStyle={{ width: 170 }}
        />
      )}

      {showProfessionPopup && (
        <SelectProfessionTypePopup
          professions={availableProfessions}
          onContinue={(professionCode) => {
            setShowProfessionPopup(false);
            browserHistory.push(routes.selectDates, {
              professionType: professionCode,
            });
          }}
          onClose={() => setShowProfessionPopup(false)}
        />
      )}
    </>
  );
}
